import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Calendar, FileText, IndianRupee, PieChart, Tag, Loader2, Filter, Download, Search } from "lucide-react";
import ExpenseManager from "../../services/dashboard/ExpenseManager";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CATEGORIES = [
  { value: 'DELIVERY_AND_LOGISTICS', label: 'Delivery & Logistics' },
  { value: 'IT_AND_DEVELOPMENT', label: 'IT & Development' },
  { value: 'OPERATIONS_AND_INVENTORY', label: 'Operations & Inventory' },
  { value: 'STAFF_AND_ADMIN', label: 'Staff & Admin' },
  { value: 'MARKETING_AND_PROMOTIONS', label: 'Marketing & Promotions' },
  { value: 'OTHER', label: 'Other' },
];

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#6B7280'];

export default function ExpenseManagement() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState("ALL");
  const [selectedMonth, setSelectedMonth] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    category: 'DELIVERY_AND_LOGISTICS',
    description: ''
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchData();
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      let expensesData = [];
      let summaryData = [];

      if (selectedYear === "ALL" || selectedMonth === "ALL") {
        expensesData = await ExpenseManager.getAllExpenses() || [];
        // Compute summary manually since we don't have an 'all-time' summary endpoint
        const summaryMap = {};
        expensesData.forEach(exp => {
          if (!summaryMap[exp.category]) {
            summaryMap[exp.category] = 0;
          }
          summaryMap[exp.category] += exp.amount;
        });
        
        summaryData = Object.keys(summaryMap).map(category => ({
          category: category,
          total: summaryMap[category]
        }));
      } else {
        const [eData, sData] = await Promise.all([
          ExpenseManager.getExpensesByMonth(selectedYear, selectedMonth),
          ExpenseManager.getMonthlyExpenseSummary(selectedYear, selectedMonth)
        ]);
        expensesData = eData || [];
        summaryData = sData || [];
      }

      setExpenses(expensesData);
      
      // Transform summary data for pie chart
      const formattedSummary = summaryData.map((item, index) => {
        const catObj = CATEGORIES.find(c => c.value === item.category);
        return {
          name: catObj ? catObj.label : item.category,
          value: item.total,
          color: COLORS[index % COLORS.length]
        };
      });
      setSummary(formattedSummary);
    } catch (error) {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingExpense(null);
    setFormData({
      title: '',
      amount: '',
      expenseDate: new Date().toISOString().split('T')[0],
      category: 'DELIVERY_AND_LOGISTICS',
      description: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      title: expense.title,
      amount: expense.amount,
      expenseDate: expense.expenseDate,
      category: expense.category,
      description: expense.description || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await ExpenseManager.updateExpense(editingExpense.expenseId, formData);
        toast.success("Expense updated successfully");
      } else {
        await ExpenseManager.createExpense(formData);
        toast.success("Expense added successfully");
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(editingExpense ? "Failed to update expense" : "Failed to add expense");
    }
  };

  const handleDelete = async (expenseId) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      try {
        await ExpenseManager.deleteExpense(expenseId);
        toast.success("Expense deleted successfully");
        fetchData();
      } catch (error) {
        toast.error("Failed to delete expense");
      }
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (expense.description && expense.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === "ALL" || expense.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleExportExcel = () => {
    if (filteredExpenses.length === 0) {
      toast.error("No data to export");
      return;
    }
    
    const exportData = filteredExpenses.map(exp => ({
      "Date": new Date(exp.expenseDate).toLocaleDateString('en-IN'),
      "Category": CATEGORIES.find(c => c.value === exp.category)?.label || exp.category,
      "Title": exp.title,
      "Amount": exp.amount,
      "Description": exp.description || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expenses");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, `Expenses_${selectedMonth}_${selectedYear}.xlsx`);
  };

  const totalExpense = summary.reduce((sum, item) => sum + item.value, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const months = [
    { value: "ALL", label: "All Months" },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: new Date(2000, i, 1).toLocaleString('default', { month: 'long' })
    }))
  ];

  const years = [
    "ALL",
    ...Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            Expense Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Track and manage monthly platform expenses</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 p-2 rounded-xl border border-gray-200 dark:border-gray-700">
            <Filter className="w-4 h-4 text-gray-500" />
            <select 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}
              className="bg-transparent border-none text-sm focus:ring-0 cursor-pointer dark:text-white"
            >
              {months.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select 
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}
              className="bg-transparent border-none text-sm focus:ring-0 cursor-pointer dark:text-white"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-sm transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </button>
          
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm transition-colors text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          
          {/* Summary Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Monthly Expense */}
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-2xl p-6 shadow-sm border border-orange-100 dark:border-orange-800 relative overflow-hidden flex flex-col justify-center min-h-[140px]">
              <div className="absolute right-[2%] top-[8%] opacity-10 pointer-events-none">
                <IndianRupee className="w-32 h-32 text-orange-500" />
              </div>
              <p className="text-orange-600 dark:text-orange-400 font-medium mb-1 text-sm">
                {selectedMonth === "ALL" && selectedYear === "ALL" ? "Total Overall Expense" : "Total Monthly Expense"}
              </p>
              <h2 className="text-3xl font-bold text-orange-900 dark:text-orange-100">{formatCurrency(totalExpense)}</h2>
              <p className="text-orange-500 dark:text-orange-400 text-xs mt-2">
                {selectedMonth === "ALL" || selectedYear === "ALL" 
                  ? "Overall Expenses" 
                  : `For ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
              </p> 
            </div>

            {/* Expense Breakdown */}
            <div className="md:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 min-h-[140px] flex items-center">
              <div className="w-1/3 pl-2">
                <h3 className="text-base font-semibold text-gray-800 dark:text-white">Expense Breakdown</h3>
                <p className="text-xs text-gray-500 mt-1">Categorized spending overview</p>
              </div>
              <div className="w-2/3 h-[120px]">
                {summary.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={summary}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={50}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {summary.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend verticalAlign="middle" align="right" layout="vertical" wrapperStyle={{ fontSize: '11px', right: 0 }}/>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    No expenses recorded
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Expenses List */}
          <div className="w-full bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Expense Records</h3>
              <span className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-xs font-semibold px-2.5 py-1 rounded-full">
                {filteredExpenses.length} Records
              </span>
            </div>
            
            {/* Filters Row */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row gap-4 bg-gray-50/50 dark:bg-gray-800/50">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full sm:w-48 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
              >
                <option value="ALL">All Categories</option>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-900/50 dark:text-gray-300">
                  <tr>
                    <th className="px-6 py-4 font-medium">Date</th>
                    <th className="px-6 py-4 font-medium">Title & Category</th>
                    <th className="px-6 py-4 font-medium">Amount</th>
                    <th className="px-6 py-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredExpenses.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                          <p>No expenses found.</p>
                          <button onClick={openAddModal} className="text-blue-500 hover:underline mt-2">Add your first expense</button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredExpenses.map((expense) => (
                      <tr key={expense.expenseId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {new Date(expense.expenseDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900 dark:text-white">{expense.title}</div>
                          <div className="flex items-center gap-1 mt-1 text-xs">
                            <Tag className="w-3 h-3 text-gray-400" />
                            <span>{CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}</span>
                          </div>
                          {expense.description && (
                            <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">{expense.description}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(expense.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(expense)}
                              className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(expense.expenseId)}
                              className="p-2 text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md p-6 border border-gray-100 dark:border-gray-800"
            >
              <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
                {editingExpense ? 'Edit Expense' : 'Add New Expense'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expense Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="e.g. Server Hosting - AWS"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (₹) <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      name="expenseDate"
                      value={formData.expenseDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category <span className="text-red-500">*</span></label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (Optional)</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Add details about this expense..."
                  ></textarea>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-sm"
                  >
                    {editingExpense ? 'Update' : 'Save Expense'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
